/*
 * ShowOnLockScreenButton.js
 * ─────────────────────────
 * One-tap "Add to Lock Screen" flow that mirrors Track FLYT Live —
 * same Uber-style Allow sheet, no widget-configuration steps.
 * Tapping the button opens a permission sheet; tapping Allow starts
 * a Profile Live Activity via the native plugin and pins the
 * athlete's FLYT ID™ card to the Lock Screen / Dynamic Island.
 *
 * Falls back gracefully on web / older iOS with a friendly toast so
 * users always know what happened.
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Smartphone, CheckCircle2, ChevronRight } from 'lucide-react';
import { getPublicOrigin, haptic } from '../lib/nativeBridge';

function isNativeApp() {
  if (typeof window === 'undefined') return false;
  return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

// iter347 · Build an absolute URL Swift's AsyncImage can fetch. Relative
// paths ("/api/files/…") and empty strings both become "" so the widget
// falls back to the initial-letter avatar. Also routes uploaded assets
// through the WebP fast-lane at a small avatar bucket so the Lock
// Screen doesn't try to download a multi-MB PNG.
function absolutizePhoto(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  if (s.startsWith('data:') || s.startsWith('blob:')) return '';
  const origin = (getPublicOrigin() || '').replace(/\/+$/, '');
  const abs = s.startsWith('http://') || s.startsWith('https://')
    ? s
    : `${origin}${s.startsWith('/') ? '' : '/'}${s}`;
  try {
    const u = new URL(abs);
    const OPT = /(?:^|\.)(emergentagent\.(?:net|com)|flythouse\.com|espncdn\.com)$/i;
    const uploaded = /\/api\/(?:files|uploads)\//i.test(abs);
    if (u.protocol === 'https:' && OPT.test(u.hostname) && (uploaded || u.hostname.includes('espncdn'))) {
      return `${origin}/api/img?src=${encodeURIComponent(abs)}&w=200`;
    }
    return abs;
  } catch (_) {
    return abs;
  }
}

export function ShowOnLockScreenButton({ athlete, className = '' }) {
  const [open, setOpen] = useState(false);
  if (!athlete?.slug) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => { haptic('medium'); setOpen(true); }}
        className={`inline-flex w-fit self-start items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white/90 transition active:scale-[0.97] ring-1 ring-emerald-400/30 hover:ring-emerald-300/60 max-w-full whitespace-nowrap overflow-hidden ${className}`}
        style={{
          background: 'linear-gradient(180deg, rgba(24,26,30,0.55) 0%, rgba(10,11,14,0.65) 100%)',
          backdropFilter: 'blur(22px) saturate(150%)',
          WebkitBackdropFilter: 'blur(22px) saturate(150%)',
        }}
        data-testid="show-on-lockscreen-btn"
      >
        <Smartphone className="w-3 h-3 text-emerald-400 shrink-0" strokeWidth={2.5} />
        <span className="truncate">Add to Lock Screen</span>
      </button>
      {open && <PermissionSheet athlete={athlete} onClose={() => setOpen(false)} />}
    </>
  );
}

function PermissionSheet({ athlete, onClose }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // iter343 · Mode picker — user chooses which experience pins to
  // their Lock Screen. Default = "auto" which lets the JS layer pick
  // based on live-game / next-game data.
  const [mode, setMode] = useState('auto');
  const MODES = [
    { key: 'auto',      label: 'Smart',   desc: 'Picks the right mode automatically' },
    { key: 'journey',   label: 'Journey', desc: 'Career Arc milestones' },
    { key: 'live_game', label: 'Live',    desc: 'Real-time score during games' },
    { key: 'stats',     label: 'Stats',   desc: 'Season stat line' },
    { key: 'story',     label: 'Story',   desc: 'Trending moments' },
    { key: 'matchup',   label: 'Matchup', desc: 'Next game preview' },
  ];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      if (!isNativeApp()) {
        // Web fallback — nothing to do natively, just acknowledge.
        toast.success('Open Flyt House™ on your iPhone to add this to your Lock Screen.', { duration: 6000 });
        onClose?.();
        return;
      }
      const { Capacitor } = await import('@capacitor/core');
      const plugin = Capacitor.Plugins?.FlytLiveActivity;
      if (!plugin?.startProfile) {
        throw new Error('This build does not yet support Lock Screen profile cards. Rebuild TestFlight.');
      }
      // iter342/iter343 · Build the rich, mode-aware payload.
      //   - User can pick a mode explicitly from the segmented control;
      //     `auto` (default) delegates to the smart selector below.
      const liveGame = athlete.live_game || null;
      const nextGame = athlete.next_game || null;
      const now = Date.now();
      const kickoffTs = nextGame?.kickoff_iso ? Date.parse(nextGame.kickoff_iso) : 0;
      const secondsToKickoff = kickoffTs ? Math.max(0, (kickoffTs - now) / 1000) : Infinity;
      let effectiveMode = mode;
      if (mode === 'auto') {
        if (liveGame && liveGame.status === 'in_progress') effectiveMode = 'live_game';
        else if (secondsToKickoff !== Infinity && secondsToKickoff < 86400 * 2) effectiveMode = 'pregame';
        else effectiveMode = 'journey';
      }

      let headline = athlete.name || 'FLYT ID™';
      let subline = athlete.school_name || athlete.team || 'FLYT ID™';
      if (effectiveMode === 'live_game' && liveGame) {
        headline = `${liveGame.home_score || 0}-${liveGame.away_score || 0} · ${liveGame.period_label || 'Live'}`;
        subline = `vs ${liveGame.opponent || 'OPP'} · ${liveGame.clock_label || 'Live'}`;
      } else if (effectiveMode === 'pregame' && nextGame) {
        const h = Math.floor(secondsToKickoff / 3600);
        const m = Math.floor((secondsToKickoff % 3600) / 60);
        headline = `Game Day · ${h > 0 ? `${h}h ` : ''}${m}m`;
        subline = `vs ${nextGame.opponent || 'OPP'} · Kickoff ${nextGame.time_label || ''}`.trim();
      } else if (effectiveMode === 'stats' && Array.isArray(athlete.stats) && athlete.stats.length) {
        const top = athlete.stats.slice(0, 3);
        headline = top.map(s => `${s.value} ${s.label}`).join(' · ');
        subline = athlete.school_name || athlete.team || '';
      } else if (effectiveMode === 'matchup' && nextGame) {
        headline = `vs ${nextGame.opponent || 'OPP'}`;
        subline = `${nextGame.date_label || ''} · ${nextGame.time_label || ''}`.trim().replace(/^·\s*|\s*·$/g, '');
      } else if (effectiveMode === 'story') {
        headline = athlete.latest_story?.title || athlete.name || 'FLYT ID™';
        subline = athlete.latest_story?.subtitle || 'TRENDING NOW';
      }

      // Arc timeline — try career_arc first, fall back to synth.
      let arc = [];
      if (Array.isArray(athlete.career_arc) && athlete.career_arc.length) {
        arc = athlete.career_arc.slice(0, 5).map((m, i, arr) => ({
          label: String(m.label || m.title || '').toUpperCase().slice(0, 6),
          subLabel: m.year ? String(m.year) : (m.date ? String(m.date).slice(0, 4) : undefined),
          active: i === arr.length - 1,
          past: i < arr.length - 1,
        }));
      } else if (Array.isArray(athlete.career_milestones) && athlete.career_milestones.length) {
        // iter350 · Same shape as career_arc, different field name.
        arc = athlete.career_milestones.slice(0, 5).map((m, i, arr) => ({
          label: String(m.label || m.title || m.name || '').toUpperCase().slice(0, 6),
          subLabel: m.year ? String(m.year) : (m.date ? String(m.date).slice(0, 4) : undefined),
          active: i === arr.length - 1,
          past: i < arr.length - 1,
        }));
      } else if (effectiveMode === 'live_game' && liveGame) {
        // Game-progress timeline replaces Arc during live play.
        arc = ['PRE', 'Q1', 'HALF', 'Q3', 'FINAL'].map((label, i) => {
          const currentQuarter = liveGame.period || 1;
          const gameIdx = liveGame.status === 'final' ? 4
            : liveGame.period_label?.toLowerCase().includes('half') ? 2
            : currentQuarter;
          return { label, active: i === gameIdx, past: i < gameIdx };
        });
      } else {
        // iter350 · Smarter synth for pro athletes — Ozzie & co had
        // an empty arc because none of high_school / commit_school /
        // pro_team were populated on their DB record. Now we also
        // pull from bio fields most pro athletes DO carry (team,
        // sport, position, draft_year, mlb_debut_year, jersey_number).
        const synth = [];
        const draftYr = athlete.draft_year || athlete.mlb_draft_year || athlete.nba_draft_year || athlete.nfl_draft_year;
        const debutYr = athlete.mlb_debut_year || athlete.nba_debut_year || athlete.nfl_debut_year || athlete.pro_debut_year;
        if (athlete.high_school) synth.push({ label: 'HS', subLabel: athlete.high_school_grad_year ? String(athlete.high_school_grad_year) : '' });
        if (athlete.college || athlete.college_school || athlete.commit_school) synth.push({ label: 'COLLEGE', subLabel: athlete.college || athlete.college_school || athlete.commit_school });
        if (draftYr) synth.push({ label: 'DRAFT', subLabel: String(draftYr) });
        if (debutYr) synth.push({ label: 'DEBUT', subLabel: String(debutYr) });
        if (athlete.pro_team || (athlete.team && athlete.sport)) synth.push({
          label: (athlete.sport || 'PRO').toString().toUpperCase().slice(0, 6),
          subLabel: (athlete.pro_team || athlete.team || '').toString(),
        });
        if (athlete.school_name && !synth.find(x => x.label === 'COLLEGE')) synth.push({ label: 'COLLEGE', subLabel: athlete.school_name });
        // Always cap with NOW so the timeline has an anchor even on
        // very sparse profiles.
        synth.push({ label: 'NOW', subLabel: (new Date()).getFullYear().toString() });
        arc = synth.slice(0, 5).map((m, i, arr) => ({
          ...m,
          active: i === arr.length - 1,
          past: i < arr.length - 1,
        }));
      }

      const res = await plugin.startProfile({
        slug: (athlete.slug || '').toLowerCase(),
        name: athlete.name || 'FLYT Athlete',
        school: athlete.school_name || athlete.team || '',
        position: athlete.position || '',
        jerseyNumber: String(athlete.jersey_number ?? ''),
        photoUrl: absolutizePhoto(athlete.photo || athlete.profile_image_url || athlete.picture),
        mode: effectiveMode,
        headline,
        subline,
        deepLinkPath: `/network/${(athlete.slug || '').toLowerCase()}`,
        arc,
      });
      if (!res?.activityId) throw new Error('Live Activity did not start');
      // iter481 · Confident success haptic — this is the "boom, done"
      // moment the user physically feels when their FLYT card lands
      // on the Lock Screen.
      haptic('success');
      toast.success('Added to your Lock Screen', {
        description: `Lock your phone to see ${athlete.name}'s FLYT ID™ card.`,
        duration: 6000,
      });
      onClose?.();
    } catch (e) {
      const msg = e?.message || 'Could not add to Lock Screen.';
      haptic('error');
      setErr(msg);
      toast.error(msg, { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  const sheet = (
    <div
      className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="show-on-lockscreen-sheet"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-10 ring-1 ring-white/10 sm:mb-8"
        style={{
          background: 'linear-gradient(180deg, rgba(20,22,26,0.98) 0%, rgba(6,8,12,1) 100%)',
        }}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-emerald-400 text-[10px] font-bold uppercase tracking-widest mb-1">FLYT ID™ · Lock Screen</p>
            <h3 className="text-white text-xl font-black leading-tight">Add {athlete.name?.split(' ')[0] || 'this'} to your Lock Screen</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition"
            aria-label="Close"
            data-testid="show-on-lockscreen-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-white/70 text-sm mb-5 leading-relaxed">
          Pin {athlete.name}&rsquo;s FLYT ID™ card to your Lock Screen and Dynamic Island. Just tap Allow — no setup needed.
        </p>

        <div className="space-y-3 mb-6">
          <Row title="Lock-Screen card" body="Their photo, name, school & position visible without opening the app." />
          <Row title="Dynamic Island preview" body="Long-press to expand into the full FLYT ID™ card." />
          <Row title="One-tap add" body="No widget setup or config to fuss with." />
        </div>

        {/* iter343 · Mode picker — segmented control lets the user
            choose which experience pins to their Lock Screen. */}
        <div className="mb-5">
          <p className="text-white/50 text-[10px] font-bold uppercase tracking-widest mb-2">Pin as</p>
          <div className="grid grid-cols-3 gap-2" data-testid="mode-picker">
            {MODES.map(m => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMode(m.key)}
                className={`px-2 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition ${mode === m.key
                  ? 'bg-emerald-400/20 text-emerald-300 ring-1 ring-emerald-400/50'
                  : 'bg-white/5 text-white/60 ring-1 ring-white/10 hover:text-white/90'}`}
                data-testid={`mode-picker-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <p className="text-white/45 text-[10px] mt-2 leading-snug">
            {(MODES.find(m => m.key === mode) || MODES[0]).desc}
          </p>
        </div>

        {err && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 ring-1 ring-red-500/40 text-red-300 text-xs" data-testid="show-on-lockscreen-err">
            {err}
          </div>
        )}

        <button
          type="button"
          onClick={accept}
          disabled={busy}
          className="w-full py-3.5 rounded-2xl bg-white text-black font-black text-sm tracking-wide active:scale-[0.98] transition disabled:opacity-60"
          data-testid="show-on-lockscreen-allow"
        >
          {busy ? 'Adding…' : 'Allow Lock-Screen Card'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="w-full mt-3 py-2.5 text-white/50 font-semibold text-sm hover:text-white transition"
          data-testid="show-on-lockscreen-not-now"
        >
          Not now
        </button>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(sheet, document.body) : sheet;
}

function Row({ title, body }) {
  return (
    <div className="flex items-start gap-3">
      <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-white font-bold text-sm leading-snug">{title}</p>
        <p className="text-white/60 text-xs leading-snug mt-0.5">{body}</p>
      </div>
    </div>
  );
}

export default ShowOnLockScreenButton;
